/* eslint-disable operator-linebreak */
import React, { memo, useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { getLocationUrl, getRoutePath } from './utilities';
import { reduxConnect } from '../../../common';
import { compose } from 'redux';
import { createRecentSearchLocation, setActiveLocation } from '../../../common/actions/locationActions';
import { LocationProvider } from '../../../common/providers/locationProvider';
import ngDeps from '../../../common/utilities/ngDependencies';
import { resetServerTrafficCamData } from '../../../common/actions/trafficCameraActions';

const mapStoreStateToProps = (storeState) => {
    const routePath = getRoutePath(storeState?.appState?.appState?.routePath);
    const { location, locationSearch, appState } = storeState ?? {};
    const { recentSearchLocations } = location ?? {};
    const { locationSearchResults, locationSuggestionIndex } = locationSearch ?? {};
    const {
        routeParams, 
        pageName,
        pageIdsToSupportLocationChanges, 
        pageId } = appState?.appState ?? {};
    
    const { layerId } = routeParams || {};

    return {
        layerId,
        routePath,
        locationSearchResults,
        locationSuggestionIndex,
        pageName,
        pageId,
        pageIdsToSupportLocationChanges,
        recentSearchLocationsLength: recentSearchLocations?.length ?? 0
    };
};

const mapDispatchToProps = {
    createRecentSearchLocation,
    setActiveLocation,
    resetServerTrafficCamData
};

const LocationSuggestionListItemComponent = ({ 
    location,
    selected,
    layerId,
    pageId,
    routePath,
    pageIdsToSupportLocationChanges,
    createRecentSearchLocation,
    recentSearchLocationsLength,
    resetServerTrafficCamData,
    setActiveLocation,
    locationSearchResults,
    locationSuggestionIndex
}) => {

    const [listItemIsSelected, setListItemIsSelected] = useState();
    const locationRouteString = location?.RouteString || location?.SlugName;
    let url = getLocationUrl(routePath, locationRouteString);
    const shouldSupportActivelLocationChanges = pageIdsToSupportLocationChanges?.includes(pageId);

    if (pageId === 'maps' && layerId && url) {
        url = `${url}/?layerId=${layerId}&center=${location?.Latitude},${location?.Longitude},7`;
    }

    useEffect(() => {
        if (locationSearchResults?.length) {
            const selectedListItem = locationSearchResults[locationSuggestionIndex];
            if (selectedListItem && locationSearchResults?.length && location?.CityId === selectedListItem?.CityId) {
                setListItemIsSelected(true);
            }else if(listItemIsSelected) {
                setListItemIsSelected(false);
            }
        }
    }, [locationSuggestionIndex, location?.CityId, locationSearchResults]);

    /**
     * TechDebt: Client side handling of active locations changes for pages like news, videos, photos, story popup, press, education, sitemap, FAQ, Partners, Privacy Policy etc...
     * that don't support location changes or breaks on location change. 
     */
    const handleActiveLocationChange = useCallback(async() => {
        if (Number.isFinite(location?.Latitude) && Number.isFinite(location?.Longitude) && shouldSupportActivelLocationChanges && pageIdsToSupportLocationChanges?.length) {
            resetServerTrafficCamData();
            const locProvider = new LocationProvider();
            const newActiveLocationDetails =  await locProvider.getLocationFromLatLng(location?.Latitude, location?.Longitude);
            if (newActiveLocationDetails?.Id) {
                const userSearchLocation = {
                    Location: {
                        Locality: location
                    },
                    Position: recentSearchLocationsLength
                };
                createRecentSearchLocation?.(userSearchLocation);
                setActiveLocation?.(newActiveLocationDetails);
                // need to update angular user state data :/
                ngDeps.user.ActiveLocation = newActiveLocationDetails;
            }
        }
    }, [
        location?.Latitude, 
        location?.Longitude, 
        shouldSupportActivelLocationChanges, 
        createRecentSearchLocation, 
        setActiveLocation, 
        recentSearchLocationsLength]);

    const suggestedLocationUrl = shouldSupportActivelLocationChanges ? '#' : url;
    return (
        <ListItem key={url} selected={listItemIsSelected || selected}>
            <LocationLink key={location?.DisplayCompositeName} href={suggestedLocationUrl}
                onMouseUp={() => {
                    handleActiveLocationChange();
                }} 
                onTouchEnd={(e) => {
                    e.stopPropagation();
                    handleActiveLocationChange();
                }}
            >
                {location?.DisplayCompositeName}
            </LocationLink>
        </ListItem>
    );
};

export const LocationSuggestionListItem = compose(reduxConnect(mapStoreStateToProps, mapDispatchToProps), memo)(LocationSuggestionListItemComponent);

LocationSuggestionListItemComponent.displayName = 'LocationSuggestionListItem';

LocationSuggestionListItemComponent.propTypes = {
    location: PropTypes.object,
    selected: PropTypes.bool,
    routePath: PropTypes.string,
    locationSearchResults: PropTypes.array,
    locationSuggestionIndex: PropTypes.number,
    busDomain: PropTypes.string,
    pageName: PropTypes.string
};

const ListItem = styled.li`
    display: flex;
    height: 54px;
    border-bottom: 1px solid rgba(216,216,216,0.5);
    border-left: 4px solid #fff;
    cursor: pointer;
    ${({ selected }) => selected && `
        color: #2e98e3;
        border-left: 4px solid #2e98e3;
    `}
    &:hover {
        background-color: #ededed;
    }
`;

const LocationLink = styled.a`
    display: block;
    padding: 16px;
    height: 100%;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: inherit;
    text-decoration: none;
`;